import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';

import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useBranch } from '../contexts/BranchContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { syncStoreWithServer, getAllFromStore } from '../utils/localDb';

import { useLanguage } from '../contexts/LanguageContext';
import { usePlanFeatures } from '../hooks/usePlanFeatures';
import PlanRenewalModal from '../components/PlanRenewalModal';

const API_URL = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost') || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:5000'
    : (process.env.REACT_APP_API_URL || 'https://dineflowbackend.onrender.com');

function IngredientsPage() {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const { formatAmount, getCurrencySymbol } = useCurrency();
    const [ingredients, setIngredients] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentIngredient, setCurrentIngredient] = useState({ unit: 'kg' });
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
    const { token, logout, currentUser, isLoading: authLoading } = useAuth();
    const { branches } = useBranch();
    const [selectedBranch, setSelectedBranchState] = useState(() => {
        const saved = localStorage.getItem('selectedBranch_ingredients');
        return saved ? (saved === 'null' ? null : parseInt(saved, 10)) : null;
    });
    const setSelectedBranch = (val) => {
        setSelectedBranchState(val);
        localStorage.setItem('selectedBranch_ingredients', val === null ? 'null' : val.toString());
    };
    // Auto-select the only branch when there's exactly one
    useEffect(() => {
        if (branches && branches.length === 1 && !selectedBranch) {
            setSelectedBranch(branches[0].id);
        }
    }, [branches]); // eslint-disable-line react-hooks/exhaustive-deps
    const [companyInfo, setCompanyInfo] = useState(null);
    const planFeatures = usePlanFeatures(companyInfo);
    const [selectedIds, setSelectedIds] = useState([]);
    const [showBranchDropdown, setShowBranchDropdown] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isProratedUpgradeOpen, setIsProratedUpgradeOpen] = useState(false);

    // Auto-Deduction Recipe Mapping state
    const [menuItems, setMenuItems] = useState([]);
    const [recipeItems, setRecipeItems] = useState([]);
    const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);
    const [expandedMenuIds, setExpandedMenuIds] = useState({});
    const [editingRecipeId, setEditingRecipeId] = useState(null);
    const [editingQty, setEditingQty] = useState('');
    const [recipeSearchTerm, setRecipeSearchTerm] = useState('');
    const [recipeForm, setRecipeForm] = useState({ menu_item_id: '', ingredient_id: '', quantity: '' });
    const [inlineForms, setInlineForms] = useState({});

    const loadRecipeData = useCallback(async () => {
        try {
            const [resM, resR] = await Promise.all([
                fetch(`${API_URL}/api/menu`, { headers: { 'Authorization': `Bearer ${token}` } }),
                fetch(`${API_URL}/api/recipe-items`, { headers: { 'Authorization': `Bearer ${token}` } })
            ]);
            const dataM = await resM.json();
            const dataR = await resR.json();
            if (dataM.success) setMenuItems(dataM.data || []);
            if (dataR.success) setRecipeItems(dataR.data || []);
        } catch (e) {
            console.error('Error fetching recipe data:', e);
        }
    }, [token]);

    const toggleAccordion = (menuId) => {
        setExpandedMenuIds(prev => ({ ...prev, [menuId]: !prev[menuId] }));
    };

    const handleUpdateRecipeItemQty = async (id, newQty) => {
        if (!newQty || isNaN(newQty) || parseFloat(newQty) <= 0) {
            showToast('Valid quantity required', 'error');
            return;
        }
        const item = recipeItems.find(r => r.id === id);
        if (!item) return;

        try {
            const res = await fetch(`${API_URL}/api/recipe-items`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    menu_item_id: item.menu_item_id,
                    ingredient_id: item.ingredient_id,
                    quantity: parseFloat(newQty)
                })
            });
            const data = await res.json();
            if (data.success) {
                showToast('Quantity updated!', 'success');
                setEditingRecipeId(null);
                loadRecipeData();
            }
        } catch (err) {
            showToast('Failed to update quantity', 'error');
        }
    };

    const handleAddInlineRule = async (menuItemId, ingredientId, quantity) => {
        if (!menuItemId || !ingredientId || !quantity) {
            showToast('Select ingredient and enter quantity', 'error');
            return;
        }
        try {
            const res = await fetch(`${API_URL}/api/recipe-items`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    menu_item_id: menuItemId,
                    ingredient_id: ingredientId,
                    quantity: parseFloat(quantity)
                })
            });
            const data = await res.json();
            if (data.success) {
                showToast('Deduction rule added!', 'success');
                loadRecipeData();
            } else {
                showToast(data.message || 'Failed to add rule', 'error');
            }
        } catch (err) {
            showToast('Failed to add rule', 'error');
        }
    };

    const handleSaveRecipeItem = async (e) => {
        e.preventDefault();
        if (!recipeForm.menu_item_id || !recipeForm.ingredient_id || !recipeForm.quantity) {
            showToast('Please select Menu Item, Ingredient and enter Quantity', 'error');
            return;
        }
        await handleAddInlineRule(recipeForm.menu_item_id, recipeForm.ingredient_id, recipeForm.quantity);
        setRecipeForm({ menu_item_id: '', ingredient_id: '', quantity: '' });
    };

    const handleDeleteRecipeItem = async (id) => {
        try {
            const res = await fetch(`${API_URL}/api/recipe-items/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                showToast('Rule removed', 'success');
                loadRecipeData();
            }
        } catch (err) {
            showToast('Failed to delete rule', 'error');
        }
    };

    const triggerImportInput = () => {
        const input = document.getElementById('ingredients-upload-input');
        if (input) input.click();
    };

    const importIngredients = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

                if (!data || data.length === 0) {
                    showToast('No data found in the file', 'error');
                    setIsUploading(false);
                    return;
                }

                const headerRow = data[0] || [];
                const linkedMenuColIndex = headerRow.findIndex(h => h && h.toString().toLowerCase().includes('linked menu'));

                // Skip header row
                const rows = data.slice(1).filter(row => row && row[0]); // Filter out empty rows or rows without names

                if (rows.length === 0) {
                    showToast('No data found in the file', 'error');
                    setIsUploading(false);
                    return;
                }

                let successCount = 0;
                let failCount = 0;

                // Process each item
                for (let i = 0; i < rows.length; i++) {
                    const row = rows[i];
                    try {
                        const name = row[0];
                        const quantity = parseFloat(row[1]) || 0;
                        const unit = row[2] || 'pcs';
                        const threshold = parseFloat(row[3]) || 0;
                        const cost = parseFloat(row[4]) || 0;
                        const linkedMenuStr = linkedMenuColIndex !== -1 ? (row[linkedMenuColIndex] || '') : '';

                        const payload = {
                            name,
                            quantity,
                            unit,
                            threshold,
                            cost_per_unit: cost,
                            branch_id: selectedBranch
                        };

                        const res = await fetch(`${API_URL}/api/ingredients`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify(payload)
                        });

                        const resData = await res.json();
                        if (resData.success) {
                            successCount++;
                            const ingId = resData.data?.id;

                            // Handle linked menu items import if present
                            if (ingId && linkedMenuStr && typeof linkedMenuStr === 'string' && linkedMenuStr.trim()) {
                                const pairs = linkedMenuStr.split(/[,;]/);
                                for (const pair of pairs) {
                                    const parts = pair.split(':');
                                    if (parts.length === 2) {
                                        const menuName = parts[0].trim();
                                        const qty = parseFloat(parts[1]);
                                        if (menuName && !isNaN(qty) && qty > 0) {
                                            const menuItem = menuItems.find(m => m.name && m.name.toLowerCase() === menuName.toLowerCase());
                                            if (menuItem) {
                                                try {
                                                    await fetch(`${API_URL}/api/recipe-items`, {
                                                        method: 'POST',
                                                        headers: {
                                                            'Content-Type': 'application/json',
                                                            'Authorization': `Bearer ${token}`
                                                        },
                                                        body: JSON.stringify({
                                                            menu_item_id: menuItem.id,
                                                            ingredient_id: ingId,
                                                            quantity: qty
                                                        })
                                                    });
                                                } catch (rErr) {
                                                    console.error('Error linking recipe item during import:', rErr);
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        } else {
                            failCount++;
                            console.error(`Failed to import ingredient ${name}:`, resData.message);
                        }
                    } catch (rowErr) {
                        failCount++;
                        console.error('Error importing row:', rowErr);
                    }
                }

                showToast(`Import completed! Success: ${successCount}, Failed: ${failCount}`, failCount > 0 ? 'warning' : 'success');
                loadIngredients();
                loadRecipeData();
            } catch (err) {
                console.error('Failed to parse excel file', err);
                showToast('Error processing file. Please check formatting.', 'error');
            } finally {
                setIsUploading(false);
                e.target.value = '';
            }
        };
        reader.readAsBinaryString(file);
    };

    // Smart upgrade handler: paid Starter users see proration modal; others navigate to pricing
    const handleUpgradeClick = () => {
        setIsProratedUpgradeOpen(true);
    };

    useEffect(() => {
        let isMounted = true;
        const fetchCompanyInfo = async () => {
            if (authLoading) return;
            try {
                const url = token
                    ? `${API_URL}/api/company/profile`
                    : `${API_URL}/api/company/public`;

                const options = token
                    ? { headers: { 'Authorization': `Bearer ${token}` } }
                    : {};

                const res = await fetch(url, options);
                const json = await res.json();
                if (isMounted && json.success && json.data) {
                    setCompanyInfo(json.data);
                }
            } catch (err) {
                console.error("Failed to fetch company info", err);
            }
        };
        fetchCompanyInfo();
        return () => { isMounted = false; };
    }, [token, authLoading]);

    // Click outside to close dropdowns
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.branch-dropdown-container')) {
                setShowBranchDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toastTimeoutRef = React.useRef(null);
    const showToast = (message, type = 'success') => {
        if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
        }
        setToast({ show: true, message, type });
        const duration = type === 'error' ? 10000 : 3000;
        toastTimeoutRef.current = setTimeout(() => {
            setToast({ show: false, message: '', type: 'success' });
            toastTimeoutRef.current = null;
        }, duration);
    };

    const loadIngredients = React.useCallback(async () => {
        setIsLoading(true);
        try {
            let url = `${API_URL}/api/ingredients`;
            if (selectedBranch) {
                url += `?branch_id=${selectedBranch}`;
            }
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.status === 401) {
                logout();
                return;
            }
            if (response.status === 403) {
                const data = await response.json();
                showToast(data.message || t('failedLoadIngredients'), 'error');
                setIngredients([]);
                return;
            }
            const data = await response.json();
            if (data.success) {
                // Precompute lowercase names and numeric fields to avoid repetitive parsing on render
                const prepared = (data.data || []).map(ing => ({
                    ...ing,
                    name_lower: ing.name ? ing.name.toLowerCase() : '',
                    quantity_num: parseFloat(ing.quantity) || 0,
                    threshold_num: parseFloat(ing.threshold) || 0
                }));
                
                // Sort ingredients alphabetically by name
                prepared.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                setIngredients(prepared);
                
                try {
                    await syncStoreWithServer('ingredients', prepared);
                } catch (e) {
                    console.error('Failed to cache ingredients:', e);
                }
            } else {
                showToast(data.message || t('failedLoadIngredients'), 'error');
                setIngredients([]);
            }
        } catch (error) {
            console.error('Error fetching ingredients:', error);
            if (!navigator.onLine) {
                // Truly offline — show cached data
                try {
                    const cached = await getAllFromStore('ingredients');
                    if (cached && cached.length > 0) {
                        setIngredients(cached);
                        showToast('Offline mode: Showing cached ingredients', 'info');
                    } else {
                        setIngredients([]);
                        showToast(t('errorLoadingIngredients'), 'error');
                    }
                } catch (cacheErr) {
                    setIngredients([]);
                }
            } else {
                // Online but backend error — don't show offline toast
                setIngredients([]);
                showToast(t('errorLoadingIngredients') || 'Failed to load ingredients. Please try again.', 'error');
            }
        } finally {
            setIsLoading(false);
        }
    }, [token, logout, selectedBranch, t]);

    useEffect(() => {
        if (token) {
            loadIngredients();
            loadRecipeData();
        }
    }, [token, loadIngredients, loadRecipeData]);


    const handleSubmit = async (e) => {
        e.preventDefault();

        // Frontend Validation
        if (!currentIngredient.name || !currentIngredient.name.trim()) {
            showToast(t('name') + ' required', 'error');
            return;
        }
        if (!currentIngredient.unit) {
            showToast('Unit is required', 'error');
            return;
        }

        const method = currentIngredient.id ? 'PUT' : 'POST';
        const url = currentIngredient.id
            ? `${API_URL}/api/ingredients/${currentIngredient.id}`
            : `${API_URL}/api/ingredients`;

        const qty = parseFloat(currentIngredient.quantity);
        const thresh = parseFloat(currentIngredient.threshold);

        const payload = {
            ...currentIngredient,
            quantity: isNaN(qty) ? 0 : qty,
            threshold: isNaN(thresh) ? 0 : thresh,
            branch_id: selectedBranch // Associate with current branch if selected
        };

        try {
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 401) {
                logout();
                return;
            }
            if (response.status === 403) {
                const data = await response.json();
                showToast(data.message || t('operationFailed'), 'error');
                return;
            }

            const data = await response.json();
            if (data.success) {
                loadIngredients();
                setIsModalOpen(false);
                setCurrentIngredient({ unit: 'kg' });
                showToast(currentIngredient.id ? t('ingredientUpdated') : t('ingredientAdded'), 'success');
            } else {
                showToast(data.message || t('operationFailed'), 'error');
            }
        } catch (error) {
            console.error('Error saving ingredient:', error);
            showToast(t('errorSavingIngredient'), 'error');
        }
    };

    const handleDelete = (id) => {
        setConfirmModal({
            show: true,
            message: t('deleteIngredientConfirm'),
            onConfirm: async () => {
                try {
                    const response = await fetch(`${API_URL}/api/ingredients/${id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                    });

                    if (response.status === 401) {
                        logout();
                        return;
                    }
                    if (response.status === 403) {
                        const data = await response.json();
                        showToast(data.message || t('failedDelete'), 'error');
                        return;
                    }

                    const data = await response.json();
                    if (data.success) {
                        loadIngredients();
                        showToast(t('ingredientDeletedSuccess') || 'Ingredient deleted successfully', 'success');
                    } else {
                        showToast(data.message || t('failedDelete'), 'error');
                    }
                } catch (error) {
                    console.error('Error deleting ingredient:', error);
                    showToast(t('errorDeletingIngredient'), 'error');
                } finally {
                    setConfirmModal({ show: false, message: '', onConfirm: null });
                }
            }
        });
    };

    const handleBulkDelete = () => {
        if (selectedIds.length === 0) return;

        setConfirmModal({
            show: true,
            message: `${t('confirmBulkDelete')} (${selectedIds.length} ${t('items')})`,
            onConfirm: async () => {
                try {
                    const response = await fetch(`${API_URL}/api/ingredients/bulk-delete`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({ ids: selectedIds })
                    });

                    if (response.status === 401) {
                        logout();
                        return;
                    }
                    if (response.status === 403) {
                        const data = await response.json();
                        showToast(data.message || t('failedDelete'), 'error');
                        return;
                    }

                    const data = await response.json();
                    if (data.success) {
                        loadIngredients();
                        setSelectedIds([]);
                        showToast(data.message || t('ingredientsDeletedSuccess'), 'success');
                    } else {
                        showToast(data.message || t('failedDelete'), 'error');
                    }
                } catch (error) {
                    console.error('Error deleting ingredients:', error);
                    showToast(t('errorDeletingIngredients'), 'error');
                } finally {
                    setConfirmModal({ show: false, message: '', onConfirm: null });
                }
            }
        });
    };

    const downloadIngredientsReport = () => {
        if (!ingredients || ingredients.length === 0) {
            alert(t('noDataAvailable') || "No data available to export.");
            return;
        }

        const reportData = [];
        const headers = [
            t('name') || "Name",
            t('quantity') || "Quantity",
            t('unit') || "Unit",
            t('threshold') || "Threshold",
            t('costPerUnit') || "Cost Per Unit",
            t('totalCost') || "Total Cost",
            t('status') || "Status",
            "Linked Menu Items"
        ];
        reportData.push(headers);

        filteredIngredients.forEach(ing => {
            const linked = recipeItems
                .filter(r => parseInt(r.ingredient_id, 10) === parseInt(ing.id, 10))
                .map(r => `${r.menu_item_name || ('Item #' + r.menu_item_id)}:${r.quantity}`)
                .join(', ');

            reportData.push([
                t(ing.name) || ing.name,
                ing.quantity_num || 0,
                ing.unit || '',
                ing.threshold_num || 0,
                ing.cost_per_unit || 0,
                (ing.quantity_num || 0) * (parseFloat(ing.cost_per_unit) || 0),
                (ing.quantity_num || 0) <= (ing.threshold_num || 0) ? (t('lowStock') || 'Low Stock') : (t('inStock') || 'In Stock'),
                linked || ''
            ]);
        });

        const ws = XLSX.utils.aoa_to_sheet(reportData);
        ws['!cols'] = headers.map(() => ({ wch: 18 }));

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Ingredients");

        const fileName = `Ingredients_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === filteredIngredients.length && filteredIngredients.length > 0) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredIngredients.map(ing => ing.id));
        }
    };

    const toggleSelectIngredient = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const filteredIngredients = React.useMemo(() => {
        if (!searchTerm) return ingredients;
        const q = searchTerm.toLowerCase();
        return ingredients.filter(ing => (ing.name || '').toLowerCase().includes(q));
    }, [ingredients, searchTerm]);

    const lowStockCount = React.useMemo(() => (
        ingredients.filter(ing => (ing.quantity_num || 0) <= (ing.threshold_num || 0)).length
    ), [ingredients]);


    const hasIngredientsPermission = (() => {
        if (authLoading) return true;
        if (!currentUser) return false;
        const roleStr = (currentUser.role || '').toLowerCase();
        if (roleStr === 'admin' || roleStr === 'owner' || roleStr === 'superadmin') return true;

        let perms = currentUser.permissions;
        if (typeof perms === 'string') {
            try { perms = JSON.parse(perms); } catch (e) { perms = {}; }
        }
        if (!perms) return false;
        return !!(perms.ingredients === true);
    })();

    const canUseIngredients = planFeatures.isTrial || planFeatures.features.ingredients;

    if (!authLoading && !hasIngredientsPermission) {
        return (
            <div className="digital-emerald-theme min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center border border-gray-100">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="fas fa-lock text-2xl text-red-600"></i>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('unauthorized')}</h1>
                    <p className="text-gray-600 mb-6">{t('noPermissionIngredients') || "You do not have permission to access the Ingredients Page."}</p>
                    <button onClick={() => navigate('/homepage')} className="w-full btn-add-highlight py-2.5 rounded-xl font-bold">
                        {t('goBackToHome') || "Go Back to Home"}
                    </button>
                </div>
            </div>
        );
    }

    if (!authLoading && !canUseIngredients) {
        return (
            <div className="digital-emerald-theme min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center border border-gray-100">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="fas fa-box-open text-2xl text-orange-600"></i>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Premium Required</h1>
                    <p className="text-gray-600 mb-6">Ingredients and inventory management are only available on the Premium plan.</p>
                    <button onClick={handleUpgradeClick} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-bold transition-all duration-200">
                        Upgrade to Premium
                    </button>
                </div>
                <PlanRenewalModal
                    isOpen={isProratedUpgradeOpen}
                    onClose={() => setIsProratedUpgradeOpen(false)}
                    companyProfile={companyInfo}
                    token={token}
                    onSuccess={() => {
                        fetch(`${API_URL}/api/company/profile`, { headers: { Authorization: `Bearer ${token}` } })
                            .then(r => r.json())
                            .then(d => { if (d.success) setCompanyInfo(d.data); })
                            .catch(() => {});
                    }}
                />
            </div>
        );
    }

    return (
        <div className="digital-emerald-theme h-screen flex flex-col overflow-hidden bg-gray-50 dark:bg-slate-950 transition-colors duration-300">
            {/* Compact Sticky Header */}
            <div className={`flex-shrink-0 z-50 shadow-lg ${!companyInfo?.banner_url ? 'bg-[#ECFDF5]' : ''}`}
                style={companyInfo?.banner_url ? {
                    backgroundImage: `linear-gradient(rgba(0,0,0,0.75), rgba(0,0,0,0.6)), url(${companyInfo.banner_url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                } : {}}
            >
                {/* Top bar: Title + Action Buttons */}
                <div className={`px-6 md:px-8 pt-5 pb-5 flex items-center justify-between gap-4 border-b border-emerald-100 ${!companyInfo?.banner_url ? 'text-emerald-950' : 'text-white'}`}>
                    <div className="flex items-center gap-3 min-w-0">
                        {companyInfo?.logo_url ? (
                            <div className="bg-white p-1 rounded-xl shadow-sm flex-shrink-0">
                                <img src={companyInfo.logo_url} alt="Logo" className="w-8 h-8 rounded-lg object-cover" />
                            </div>
                        ) : (
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${!companyInfo?.banner_url ? 'bg-emerald-100 text-emerald-600' : 'bg-white/20 text-white'}`}>
                                <i className="fas fa-box-open text-base"></i>
                            </div>
                        )}
                        <div className="min-w-0">
                            <h1 className="text-base md:text-lg font-bold tracking-tight leading-tight truncate">{t('ingredientsManagement')}</h1>
                            <p className={`text-xs font-medium leading-tight ${!companyInfo?.banner_url ? 'text-emerald-700' : 'text-white/75'}`}>{t('manageInventory')}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button 
                            onClick={triggerImportInput} 
                            disabled={isUploading}
                            className="h-9 px-3 bg-white hover:bg-emerald-50 text-emerald-600 rounded-xl shadow border border-emerald-200 transition flex items-center gap-2 text-xs font-bold active:scale-95"
                        >
                            <i className={`fas ${isUploading ? 'fa-spinner animate-spin' : 'fa-file-upload'} text-sm`}></i>
                            <span className="hidden sm:inline">Import Excel</span>
                        </button>
                        <input
                            type="file"
                            id="ingredients-upload-input"
                            accept=".xlsx,.xls"
                            onChange={importIngredients}
                            className="hidden"
                        />
                        <button onClick={downloadIngredientsReport} className="h-9 px-3 bg-white hover:bg-emerald-50 text-emerald-600 rounded-xl shadow border border-emerald-200 transition flex items-center gap-2 text-xs font-bold active:scale-95">
                            <i className="fas fa-file-excel text-sm"></i>
                            <span className="hidden sm:inline">Export Excel</span>
                        </button>
                        <button onClick={loadIngredients} className="w-9 h-9 flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow border border-emerald-500 transition active:scale-95 flex-shrink-0" title={t('refresh')}>
                            <i className={`fas fa-sync-alt text-xs ${isLoading ? 'animate-spin' : ''}`}></i>
                        </button>
                        {(parseInt(companyInfo?.id || companyInfo?.company_id, 10) === 39) && !!companyInfo?.auto_deduct_inventory && (
                            <button
                                onClick={() => { loadRecipeData(); setIsRecipeModalOpen(true); }}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white h-9 px-3 rounded-xl text-xs font-bold transition-all shadow-lg active:scale-95 flex items-center gap-1.5"
                            >
                                <i className="fas fa-link text-xs"></i>
                                <span className="hidden sm:inline">Link Stock Rules</span>
                            </button>
                        )}
                        <button
                            onClick={() => { setCurrentIngredient({ unit: 'kg', threshold: 5 }); setIsModalOpen(true); }}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-3 rounded-xl text-xs font-bold transition-all shadow-lg active:scale-95 flex items-center gap-1.5"
                        >
                            <i className="fas fa-plus text-xs"></i>
                            <span className="hidden sm:inline">{t('add') || 'Add'}</span>
                        </button>
                        {selectedIds.length > 1 && (
                            <button onClick={handleBulkDelete} className="bg-red-500 hover:bg-red-600 text-white h-9 px-3 rounded-xl text-xs font-bold transition-all shadow active:scale-95 flex items-center gap-1.5 animate-pulse">
                                <i className="fas fa-trash-alt text-xs"></i>
                                <span>{t('delete') || 'Delete'} ({selectedIds.length})</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Bottom bar: Branch Selector */}
                <div className="px-6 md:px-8 pt-4 pb-4 flex flex-wrap items-center gap-3">
                    <div className="relative branch-dropdown-container flex-shrink-0">
                        <button
                            onClick={() => setShowBranchDropdown(!showBranchDropdown)}
                            className="bg-white border border-emerald-200 rounded-xl px-3 py-2 flex items-center gap-2 hover:bg-emerald-50 transition-all text-emerald-900 shadow-sm text-xs font-bold"
                        >
                            <div className="w-5 h-5 rounded-md bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                <i className="fas fa-university text-[9px] text-emerald-600"></i>
                            </div>
                            <span className="truncate max-w-[150px]">
                                {selectedBranch ? (branches.find(b => b.id === selectedBranch)?.name || t('branch')) : t('allBranchesCombined') || 'All Branches'}
                            </span>
                            <i className={`fas fa-chevron-down text-emerald-400 text-[9px] transition-transform duration-300 ${showBranchDropdown ? 'rotate-180' : ''}`}></i>
                        </button>
                        {showBranchDropdown && (
                            <div className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-800 py-1.5 z-[100] animate-fade-in max-h-64 overflow-y-auto">
                                <div className="px-3 py-1.5 border-b border-gray-100 mb-1">
                                    <p className="text-[10px] font-bold text-gray-400">{t('selectBranch') || 'Select Location'}</p>
                                </div>
                                {branches && branches.length > 1 && (
                                <button onClick={() => { setSelectedBranch(null); setShowBranchDropdown(false); }} className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 ${!selectedBranch ? 'text-emerald-600 font-bold bg-emerald-50' : 'text-gray-700 font-semibold hover:bg-emerald-50'}`}>
                                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${!selectedBranch ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-400'}`}><i className="fas fa-globe text-[9px]"></i></div>
                                    <span>{t('allBranchesCombined') || 'All Branches'}</span>
                                </button>
                                )}
                                {(branches || []).map(b => (
                                    <button key={b.id} onClick={() => { setSelectedBranch(b.id); setShowBranchDropdown(false); }} className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 ${selectedBranch === b.id ? 'text-emerald-600 font-bold bg-emerald-50' : 'text-gray-700 font-semibold hover:bg-emerald-50'}`}>
                                        <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${selectedBranch === b.id ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-400'}`}><i className="fas fa-store text-[9px]"></i></div>
                                        <span className="truncate">{b.name}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto">
                <div className="container mx-auto px-4 md:px-6 mt-4">
                {/* High-Density Inventory Pulse Strip - One Line Summary & Search */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl md:rounded-[2rem] shadow-xl border border-gray-100 dark:border-slate-800 p-2 mb-8 flex flex-col md:flex-row items-stretch gap-2 md:gap-0 md:divide-x divide-gray-50 dark:divide-slate-800">
                    {/* Total Ingredients Card */}
                    <div className="flex-1 min-w-0 md:min-w-[240px] bg-white dark:bg-slate-900 rounded-xl md:rounded-[2rem] shadow-sm border border-gray-100 dark:border-slate-800 p-4 md:p-6 flex items-center gap-3 md:gap-5 hover:bg-emerald-50/50 dark:hover:bg-slate-800/50 transition-all duration-300 md:hover:-translate-y-1 md:hover:shadow-lg expand-in group" style={{ animationDelay: '0.1s' }}>
                        <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-white dark:bg-slate-800 flex items-center justify-center text-emerald-600 dark:text-emerald-400 flex-shrink-0 shadow-sm border border-emerald-100 dark:border-emerald-800 group-hover:scale-110 transition-transform">
                            <i className="fas fa-cubes text-base md:text-lg"></i>
                        </div>
                        <div className="flex flex-col min-w-0">
                            <span className="text-sm font-semibold text-gray-500 dark:text-slate-400 tracking-normal leading-none mb-1 md:mb-1.5">{t('totalIngredients')}</span>
                            <span className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white tracking-tighter truncate">{ingredients.length}</span>
                        </div>
                    </div>

                    {/* Low Stock Items Card */}
                    <div className="flex-1 min-w-0 md:min-w-[240px] bg-white dark:bg-slate-900 rounded-xl md:rounded-[2rem] shadow-sm border border-gray-100 dark:border-slate-800 p-4 md:p-6 flex items-center gap-3 md:gap-5 hover:bg-orange-50/50 dark:hover:bg-slate-800/50 transition-all duration-300 md:hover:-translate-y-1 md:hover:shadow-lg expand-in group" style={{ animationDelay: '0.2s' }}>
                        <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-white dark:bg-slate-800 flex items-center justify-center text-orange-500 dark:text-orange-400 flex-shrink-0 shadow-sm border border-orange-100 dark:border-orange-800 group-hover:scale-110 transition-transform">
                            <i className="fas fa-exclamation-triangle text-base md:text-lg"></i>
                        </div>
                        <div className="flex flex-col min-w-0">
                            <span className="text-sm font-semibold text-gray-500 dark:text-slate-400 tracking-normal leading-none mb-1 md:mb-1.5">{t('lowStockItems')}</span>
                            <span className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white tracking-tighter truncate">{lowStockCount}</span>
                        </div>
                    </div>

                    {/* Quick Search Card - Integrated Style */}
                    <div className="flex-[2] min-w-0 md:min-w-[350px] bg-white dark:bg-slate-900 rounded-xl md:rounded-[2rem] shadow-sm border border-gray-100 dark:border-slate-800 p-2 flex items-center relative expand-in" style={{ animationDelay: '0.3s' }}>
                        <div className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-gray-50 dark:bg-slate-950 flex items-center justify-center text-gray-400 dark:text-slate-500">
                            <i className="fas fa-search text-xs md:text-sm"></i>
                        </div>
                        <input
                            type="text"
                            placeholder={t('searchIngredients') || "Search ingredients..."}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-14 md:pl-20 pr-4 md:pr-8 py-3 md:py-5 bg-transparent border-none text-xs md:text-sm font-bold focus:ring-0 outline-none text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-slate-400 transition-all"
                        />
                    </div>
                </div>

                {/* Ingredients Table */}
                <div className="expand-in" style={{ animationDelay: '0.4s' }}>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 dark:bg-slate-800/50 border-b border-gray-100 dark:border-slate-700">
                                <tr>
                                    <th className="p-4 w-10">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 text-emerald-600 rounded cursor-pointer bg-transparent border-gray-300 dark:border-slate-600"
                                            checked={filteredIngredients.length > 0 && selectedIds.length === filteredIngredients.length}
                                            onChange={toggleSelectAll}
                                        />
                                    </th>
                                    <th className="p-4 font-semibold text-gray-600 dark:text-slate-200">{t('name')}</th>
                                    <th className="p-4 font-semibold text-gray-600 dark:text-slate-200">{t('quantity')}</th>
                                    <th className="p-4 font-semibold text-gray-600 dark:text-slate-200">{t('unit')}</th>
                                    <th className="p-4 font-semibold text-gray-600 dark:text-slate-200">{t('threshold')}</th>
                                    <th className="p-4 font-semibold text-gray-600 dark:text-slate-200 whitespace-nowrap">{t('costPerUnit')}</th>
                                    <th className="p-4 font-semibold text-gray-600 dark:text-slate-200 whitespace-nowrap">{t('totalCost')}</th>
                                    <th className="p-4 font-semibold text-gray-600 dark:text-slate-200">{t('status')}</th>
                                    <th className="p-4 font-semibold text-gray-600 dark:text-slate-200 text-right">{t('actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-slate-800 text-sm">
                                {isLoading ? (
                                    <tr><td colSpan="9" className="text-center py-8 text-gray-500 dark:text-slate-400">{t('loading')}</td></tr>
                                ) : filteredIngredients.length === 0 ? (
                                    <tr><td colSpan="9" className="text-center py-16 font-normal" style={{ color: '#dc2626' }}>No items found. Try adjusting filters.</td></tr>
                                ) : (
                                    filteredIngredients.map((ing, idx) => (
                                        <tr key={ing.id} className={`hover:bg-gray-50 dark:hover:bg-slate-800/50 transition ${selectedIds.includes(ing.id) ? 'bg-emerald-50/50 dark:bg-emerald-950/20' : ''}`}>
                                            <td className="p-4">
                                                <input
                                                    type="checkbox"
                                                    className="w-4 h-4 text-emerald-600 rounded cursor-pointer bg-transparent border-gray-300 dark:border-slate-600"
                                                    checked={selectedIds.includes(ing.id)}
                                                    onChange={() => toggleSelectIngredient(ing.id)}
                                                />
                                            </td>
                                            <td className="p-4 font-medium text-gray-900 dark:text-white">
                                                <div>{t(ing.name)}</div>
                                            </td>
                                            <td className="p-4 text-gray-600 dark:text-slate-400">{ing.quantity}</td>
                                            <td className="p-4 text-gray-600 dark:text-slate-400">{ing.unit}</td>
                                            <td className="p-4 text-gray-600 dark:text-slate-400">{ing.threshold}</td>
                                            <td className="p-4 text-gray-600 dark:text-slate-400 whitespace-nowrap">{formatAmount(ing.cost_per_unit || 0)}</td>
                                            <td className="p-4 text-gray-600 dark:text-slate-400 font-semibold whitespace-nowrap">{formatAmount((ing.quantity_num || 0) * (parseFloat(ing.cost_per_unit) || 0))}</td>
                                            <td className="p-4">
                                                {(ing.quantity_num || 0) <= (ing.threshold_num || 0) ? (
                                                    <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap">{t('lowStock')}</span>
                                                ) : (
                                                    <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap">{t('inStock')}</span>
                                                )}
                                            </td>
                                            <td className="p-4 text-right space-x-1 whitespace-nowrap">
                                                <button
                                                    onClick={() => { setCurrentIngredient(ing); setIsModalOpen(true); }}
                                                    className="text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 p-1.5 rounded-lg transition"
                                                    title={t('edit')}
                                                >
                                                    <i className="fas fa-edit text-xs"></i>
                                                </button>
                                                {selectedIds.length === 1 && selectedIds.includes(ing.id) && (
                                                    <button
                                                        onClick={() => handleDelete(ing.id)}
                                                        className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 p-1.5 rounded-lg transition"
                                                        title={t('delete')}
                                                    >
                                                        <i className="fas fa-trash-alt text-xs"></i>
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {
                isModalOpen && (
                    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl w-full max-w-md p-6 transform transition-all scale-100 border border-white/10 max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                    {currentIngredient.id ? t('editIngredient') : t('addIngredient')}
                                </h2>
                                <button onClick={() => setIsModalOpen(false)} className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition">
                                    <i className="fas fa-times text-xl"></i>
                                </button>
                            </div>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">{t('name') || 'Name'}</label>
                                    <input
                                        type="text"
                                        required
                                        value={currentIngredient.name || ''}
                                        onChange={e => setCurrentIngredient({ ...currentIngredient, name: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-800 bg-white dark:bg-slate-950 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition"
                                        placeholder={t('ingredientNamePlaceholder') || "Ingredient Name (e.g., Tomato)"}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">{t('quantity')}</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            required
                                            value={currentIngredient.quantity || ''}
                                            onChange={e => setCurrentIngredient({ ...currentIngredient, quantity: e.target.value })}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-800 bg-white dark:bg-slate-950 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition"
                                            placeholder={t('quantityPlaceholder') || "0.00"}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">{t('unit')}</label>
                                        <select
                                            value={currentIngredient.unit || 'kg'}
                                            onChange={e => setCurrentIngredient({ ...currentIngredient, unit: e.target.value })}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-800 bg-white dark:bg-slate-950 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition"
                                        >
                                            <option value="kg">kg</option>
                                            <option value="g">g</option>
                                            <option value="l">l</option>
                                            <option value="ml">ml</option>
                                            <option value="pcs">pcs</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Cost Per Unit ({getCurrencySymbol()})</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={currentIngredient.cost_per_unit || ''}
                                        onChange={e => setCurrentIngredient({ ...currentIngredient, cost_per_unit: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-800 bg-white dark:bg-slate-950 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition"
                                        placeholder="0.00"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Total Cost ({getCurrencySymbol()})</label>
                                    <input
                                        type="text"
                                        readOnly
                                        value={formatAmount((parseFloat(currentIngredient.quantity) || 0) * (parseFloat(currentIngredient.cost_per_unit) || 0))}
                                        className="w-full px-4 py-2 rounded-lg border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-900 text-gray-500 dark:text-slate-400 outline-none transition font-bold"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">{t('lowStockThreshold')}</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        required
                                        value={currentIngredient.threshold !== undefined && currentIngredient.threshold !== null ? currentIngredient.threshold : 5}
                                        onChange={e => setCurrentIngredient({ ...currentIngredient, threshold: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-800 bg-white dark:bg-slate-950 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition"
                                        placeholder={t('thresholdPlaceholder') || "Low Stock Alert at..."}
                                    />
                                </div>

                                <div className="pt-4 flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-700 text-gray-700 dark:text-slate-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition font-medium"
                                    >
                                        {t('cancel')}
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition font-medium shadow-md"
                                    >
                                        {t('saveIngredient')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* Toast Notification */}
            {
                toast.show && (
                    <div className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white transform transition-all duration-300 z-50 ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
                        }`}>
                        <div className="flex items-center gap-2">
                            <i className={`fas ${toast.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                            <span>{toast.message}</span>
                        </div>
                    </div>
                )
            }

            {/* Confirm Modal */}
            {
                confirmModal.show && (
                    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-sm p-6 transform transition-all scale-100 border border-white/10">
                            <div className="text-center mb-6">
                                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400 text-xl"></i>
                                </div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{t('confirmAction')}</h3>
                                <p className="text-gray-600 dark:text-slate-400">{confirmModal.message || t('deleteIngredientConfirm')}</p>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
                                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-700 text-gray-700 dark:text-slate-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition font-medium"
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    onClick={confirmModal.onConfirm}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium shadow-md"
                                >
                                    {t('delete')}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Recipe / Stock Deduction Mapping Modal */}
            {isRecipeModalOpen && (
                <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center p-4 z-50 backdrop-blur-sm" onClick={() => setIsRecipeModalOpen(false)}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-2xl w-full p-6 modal-content shadow-2xl border border-gray-100 dark:border-slate-800 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        {/* Header */}
                        <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-100 dark:border-slate-800 flex-shrink-0">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                    <i className="fas fa-link text-indigo-500"></i> Auto Stock Deduction Rules
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-slate-400">Map menu items to inventory items to auto-deduct stock when orders are ready.</p>
                            </div>
                            <button onClick={() => setIsRecipeModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-lg p-1">
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        {/* Search & Quick Global Add */}
                        <div className="space-y-3 mb-4 flex-shrink-0">
                            <form onSubmit={handleSaveRecipeItem} className="bg-emerald-50/60 dark:bg-slate-800/60 p-3.5 rounded-xl border border-emerald-100 dark:border-slate-700">
                                <h4 className="text-xs font-bold text-emerald-800 dark:text-emerald-300 uppercase tracking-wider mb-2">Quick Add Rule</h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 mb-2">
                                    <div>
                                        <select
                                            value={recipeForm.menu_item_id}
                                            onChange={(e) => setRecipeForm({ ...recipeForm, menu_item_id: e.target.value })}
                                            className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-lg text-xs font-medium text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                                            required
                                        >
                                            <option value="">-- Menu Item --</option>
                                            {menuItems.map((item) => (
                                                <option key={item.id} value={item.id}>{item.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <select
                                            value={recipeForm.ingredient_id}
                                            onChange={(e) => setRecipeForm({ ...recipeForm, ingredient_id: e.target.value })}
                                            className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-lg text-xs font-medium text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                                            required
                                        >
                                            <option value="">-- Inventory Item --</option>
                                            {ingredients.map((ing) => (
                                                <option key={ing.id} value={ing.id}>{ing.name} ({ing.unit})</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <input
                                            type="number"
                                            step="any"
                                            min="0.001"
                                            placeholder="Qty (e.g. 1.0)"
                                            value={recipeForm.quantity}
                                            onChange={(e) => setRecipeForm({ ...recipeForm, quantity: e.target.value })}
                                            className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-lg text-xs font-medium text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                                            required
                                        />
                                    </div>
                                </div>
                                <button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-1.5 rounded-lg text-xs transition shadow-sm flex items-center justify-center gap-1.5">
                                    <i className="fas fa-plus text-xs"></i> Save Deduction Rule
                                </button>
                            </form>

                            {/* Search Filter */}
                            <div className="relative">
                                <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                                <input
                                    type="text"
                                    placeholder="Filter menu items or ingredients..."
                                    value={recipeSearchTerm}
                                    onChange={(e) => setRecipeSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-medium text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                        </div>

                        {/* Accordion Menu Items List */}
                        <div className="flex-1 overflow-y-auto pr-1 space-y-2.5">
                            {menuItems.filter(m => {
                                if (!recipeSearchTerm) return true;
                                const q = recipeSearchTerm.toLowerCase();
                                const mNameMatch = (m.name || '').toLowerCase().includes(q);
                                const ingMatch = recipeItems.some(r => r.menu_item_id === m.id && (r.ingredient_name || '').toLowerCase().includes(q));
                                return mNameMatch || ingMatch;
                            }).length === 0 ? (
                                <p className="text-xs text-gray-400 text-center py-6">No menu items found matching search.</p>
                            ) : (
                                menuItems.filter(m => {
                                    if (!recipeSearchTerm) return true;
                                    const q = recipeSearchTerm.toLowerCase();
                                    const mNameMatch = (m.name || '').toLowerCase().includes(q);
                                    const ingMatch = recipeItems.some(r => r.menu_item_id === m.id && (r.ingredient_name || '').toLowerCase().includes(q));
                                    return mNameMatch || ingMatch;
                                }).map((m) => {
                                    const rules = recipeItems.filter(r => parseInt(r.menu_item_id, 10) === parseInt(m.id, 10));
                                    const isExpanded = !!expandedMenuIds[m.id];
                                    const inlineState = inlineForms[m.id] || { ingredient_id: '', quantity: '' };

                                    return (
                                        <div key={m.id} className="border border-gray-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-900 shadow-sm transition">
                                            {/* Accordion Header */}
                                            <button
                                                onClick={() => toggleAccordion(m.id)}
                                                className="w-full text-left p-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-800/50 transition font-medium"
                                            >
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="font-bold text-xs text-gray-900 dark:text-white truncate">{m.name}</span>
                                                    {m.category && <span className="text-[10px] bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400 px-2 py-0.5 rounded-md font-semibold truncate">{m.category}</span>}
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${rules.length > 0 ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400 border border-indigo-200/50' : 'bg-gray-100 text-gray-400 dark:bg-slate-800'}`}>
                                                        {rules.length} {rules.length === 1 ? 'ingredient' : 'ingredients'}
                                                    </span>
                                                    <i className={`fas fa-chevron-down text-xs text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180 text-indigo-500' : ''}`}></i>
                                                </div>
                                            </button>

                                            {/* Accordion Body */}
                                            {isExpanded && (
                                                <div className="p-3 bg-gray-50/50 dark:bg-slate-800/30 border-t border-gray-100 dark:border-slate-800 space-y-2">
                                                    {rules.length === 0 ? (
                                                        <p className="text-[11px] text-gray-400 italic py-1 text-center">No ingredients linked yet.</p>
                                                    ) : (
                                                        <div className="space-y-1.5">
                                                            {rules.map((r) => (
                                                                <div key={r.id} className="flex items-center justify-between bg-white dark:bg-slate-900 p-2 rounded-lg border border-gray-100 dark:border-slate-800 text-xs">
                                                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                                                        <i className="fas fa-cube text-emerald-500 text-[10px]"></i>
                                                                        <span className="font-semibold text-gray-800 dark:text-slate-200 truncate">{r.ingredient_name}</span>
                                                                    </div>

                                                                    {editingRecipeId === r.id ? (
                                                                        <div className="flex items-center gap-1.5">
                                                                            <input
                                                                                type="number"
                                                                                step="any"
                                                                                className="w-16 px-1.5 py-0.5 text-xs bg-gray-50 dark:bg-slate-800 border border-emerald-500 rounded text-gray-900 dark:text-white"
                                                                                value={editingQty}
                                                                                onChange={(e) => setEditingQty(e.target.value)}
                                                                            />
                                                                            <span className="text-[10px] text-gray-500">{r.unit}</span>
                                                                            <button
                                                                                onClick={() => handleUpdateRecipeItemQty(r.id, editingQty)}
                                                                                className="text-emerald-600 hover:text-emerald-700 p-1 text-xs"
                                                                                title="Save"
                                                                            >
                                                                                <i className="fas fa-check"></i>
                                                                            </button>
                                                                            <button
                                                                                onClick={() => setEditingRecipeId(null)}
                                                                                className="text-gray-400 hover:text-gray-600 p-1 text-xs"
                                                                                title="Cancel"
                                                                            >
                                                                                <i className="fas fa-times"></i>
                                                                            </button>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="font-bold text-emerald-600 dark:text-emerald-400 text-xs">{r.quantity} {r.unit}</span>
                                                                            <button
                                                                                onClick={() => { setEditingRecipeId(r.id); setEditingQty(r.quantity); }}
                                                                                className="text-gray-400 hover:text-indigo-600 p-1 rounded transition"
                                                                                title="Edit quantity"
                                                                            >
                                                                                <i className="fas fa-pen text-[10px]"></i>
                                                                            </button>
                                                                            <button
                                                                                onClick={() => handleDeleteRecipeItem(r.id)}
                                                                                className="text-gray-400 hover:text-red-600 p-1 rounded transition"
                                                                                title="Delete rule"
                                                                            >
                                                                                <i className="fas fa-trash-alt text-[10px]"></i>
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Add Ingredient directly inside this Accordion */}
                                                    <div className="pt-2 border-t border-dashed border-gray-200 dark:border-slate-700 flex items-center gap-2">
                                                        <select
                                                            value={inlineState.ingredient_id}
                                                            onChange={(e) => setInlineForms(prev => ({
                                                                ...prev,
                                                                [m.id]: { ...prev[m.id], ingredient_id: e.target.value }
                                                            }))}
                                                            className="flex-1 px-2 py-1 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-lg text-[11px] text-gray-800 dark:text-white"
                                                        >
                                                            <option value="">+ Add Ingredient...</option>
                                                            {ingredients.map((ing) => (
                                                                <option key={ing.id} value={ing.id}>{ing.name} ({ing.unit})</option>
                                                            ))}
                                                        </select>
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            placeholder="Qty"
                                                            value={inlineState.quantity || ''}
                                                            onChange={(e) => setInlineForms(prev => ({
                                                                ...prev,
                                                                [m.id]: { ...prev[m.id], quantity: e.target.value }
                                                            }))}
                                                            className="w-16 px-2 py-1 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-lg text-[11px] text-gray-800 dark:text-white"
                                                        />
                                                        <button
                                                            onClick={async () => {
                                                                if (inlineState.ingredient_id && inlineState.quantity) {
                                                                    await handleAddInlineRule(m.id, inlineState.ingredient_id, inlineState.quantity);
                                                                    setInlineForms(prev => ({
                                                                        ...prev,
                                                                        [m.id]: { ingredient_id: '', quantity: '' }
                                                                    }));
                                                                } else {
                                                                    showToast('Select ingredient and enter quantity', 'error');
                                                                }
                                                            }}
                                                            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-2.5 py-1 rounded-lg text-[11px] transition shadow-sm flex-shrink-0"
                                                        >
                                                            Add
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            )}
            </div>
        </div >
    );
}

export default IngredientsPage;





